import { useIntl } from '@leagueplatform/locales';
import * as React from 'react';
import {
  Button,
  HeadingBar,
  Modal,
  VisuallyHidden,
} from '@leagueplatform/genesis-core';
import { LegacyOrderNewCardAnalyticsData } from '#types/analytics.types.ts';
import { Dependent } from '#types/dependents.types.ts';
import { LegacyOrderNewCardForm } from './order-new-card-form.component';

/**
 * @deprecated This component is deprecated and will be removed in a future version.
 */

export const LegacyOrderNewCard = ({
  benefitId,
  isOpen,
  onComplete,
  dependents,
  elementToFocusOnClose: elementToFocusOnCloseProp,
  vendorId,
  title,
  benefitSource,
  productType,
}: {
  benefitId: string;
  isOpen: boolean;
  onComplete?: () => void;
  dependents: Dependent[];
  elementToFocusOnClose?: HTMLElement | null;
  vendorId: string;
  title: string;
  benefitSource: string;
  productType: string;
}) => {
  const { formatMessage } = useIntl();
  const [isShown, setIsShown] = React.useState(isOpen);
  const [isExiting, setIsExiting] = React.useState(false);
  const [isConfirmExitFlagEnabled, setIsConfirmExitFlagEnabled] =
    React.useState(true);
  const elementToFocusOnClose = React.useRef(elementToFocusOnCloseProp);
  const analyticsData: LegacyOrderNewCardAnalyticsData = React.useMemo(
    () => ({
      benefitId,
      benefitName: title,
      benefitSource,
      productType,
      vendorId,
    }),
    [benefitId, vendorId, title, benefitSource, productType],
  );

  const handleClose = () => {
    if (isConfirmExitFlagEnabled && !isExiting) {
      setIsExiting(true);
      return;
    }

    setIsShown(false);
    setIsExiting(false);
    setIsConfirmExitFlagEnabled(true);
    onComplete?.();

    if (elementToFocusOnClose.current) {
      setTimeout(() => {
        elementToFocusOnClose.current?.focus();
      }, 0);
    }
  };

  const handleContinueOrder = () => {
    setIsExiting(false);
  };

  const disableConfirmExitFlag = () => {
    setIsConfirmExitFlagEnabled(false);
  };

  return (
    <Modal.Root
      open={isShown}
      onOpenChange={(isOpenChange) => {
        // pressing `esc` sets modal state of open to false
        if (isOpenChange === false) {
          handleClose();
          return;
        }
        setIsShown(true);
      }}
    >
      <Modal.Content
        forceModalFocus
        layout="fullscreen"
        css={{
          '.GDS-modal': {
            width: '100vw',
            insetInlineStart: '0',
            height: '100vh',
            borderRadius: '$none',
            backgroundColor: '$surfaceBackgroundTertiary',
          },
          '.GDS-modal-close-button-wrapper': {
            display: 'none',
          },
        }}
      >
        <HeadingBar
          css={{ backgroundColor: 'transparent' }}
          trailingAction={
            <Button
              css={{ '& > *': { paddingInlineEnd: 0 } }}
              priority="secondary"
              quiet
              icon="tinyClose"
              size="medium"
              onClick={handleClose}
            >
              <VisuallyHidden>{formatMessage({ id: 'CLOSE' })}</VisuallyHidden>
            </Button>
          }
        />
        <Modal.Title>
          <VisuallyHidden>
            {formatMessage({ id: 'ORDER_NEW_CARD_HEADING' })}
          </VisuallyHidden>
        </Modal.Title>

        <Modal.Description>
          <LegacyOrderNewCardForm
            isExiting={isExiting}
            cancelFunction={handleClose}
            continueFunction={handleContinueOrder}
            disableConfirmExitFlag={disableConfirmExitFlag}
            benefitId={benefitId}
            dependents={dependents}
            analyticsData={analyticsData}
          />
        </Modal.Description>
      </Modal.Content>
    </Modal.Root>
  );
};
